import React from 'react';
import Card from '../../../components/ui/Card';
import { translateTerm as translateTermUtil, fmtBRL, fmtNum } from '../utils';

export default function OverviewTab({ lang, year, petrobrasData, refDate, t, setPolicyModal }) {
  const translateTerm = (term) => translateTermUtil(term, lang);

  const history = petrobrasData['historico_emissor']?.[0] || {};
  const genInfo = petrobrasData[`${year}.csv`]?.[0] || {};
  const capitalData = petrobrasData['capital_social'] || [];
  const primaryCap = capitalData[0] || {};

  const totalShares = parseFloat(primaryCap.Quantidade_Total_Acoes) || 0;
  const ordShares = parseFloat(primaryCap.Quantidade_Acoes_Ordinarias) || 0;
  const prefShares = parseFloat(primaryCap.Quantidade_Acoes_Preferenciais) || 0;

  const ordPct = totalShares > 0 ? (ordShares / totalShares) * 100 : 0;
  const prefPct = totalShares > 0 ? (prefShares / totalShares) * 100 : 0;

  const auditor = petrobrasData['auditor']?.[0] || {};
  const auditorResp = petrobrasData['auditor_responsavel']?.[0] || {};

  return (
    <div className="space-y-6">

      {/* Row 1: Corporate Profile & Auditor Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Visual Company Registration Card */}
        <div className="lg:col-span-2 bg-[var(--card)] border border-[var(--border)] rounded-2xl p-6 shadow-md hover:-translate-y-1 hover:shadow-xl transition-all duration-300 relative overflow-hidden group">
          <div className="absolute -right-16 -top-16 w-36 h-36 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/15 transition-all duration-300" />

          <div className="flex justify-between items-start mb-4">
            <div>
              <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                {translateTerm(history.Forma_Constituicao_Emissor) || 'Sociedade Anônima'}
              </span>
              <h3 className="text-xl font-extrabold text-[var(--text)] mt-2">
                {genInfo.DENOM_CIA || history.Nome_Companhia || 'PETROLEO BRASILEIRO S.A. PETROBRAS'}
              </h3>
              <p className="text-xs text-[var(--muted)] mt-1">
                {lang === 'pt' ? 'Setor: Petróleo, Gás e Biocombustíveis' : 'Sector: Oil, Gas & Biofuels'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 border-t border-[var(--border)] pt-5">
            <div className="space-y-1">
              <span className="text-[10px] text-[var(--muted)] uppercase font-semibold tracking-wider block">CNPJ</span>
              <span className="text-xs font-bold text-[var(--text)] font-mono block">33.000.167/0001-01</span>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] text-[var(--muted)] uppercase font-semibold tracking-wider block">{lang === 'pt' ? 'Código CVM' : 'CVM Code'}</span>
              <span className="text-xs font-bold text-emerald-500 font-mono block">{genInfo.CD_CVM || '-'}</span>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] text-[var(--muted)] uppercase font-semibold tracking-wider block">{lang === 'pt' ? 'Doc CVM' : 'CVM Doc'}</span>
              <span className="text-xs font-bold text-[var(--text)] block">{genInfo.CATEG_DOC || '-'}</span>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] text-[var(--muted)] uppercase font-semibold tracking-wider block">{lang === 'pt' ? 'Recebido em' : 'Received Date'}</span>
              <span className="text-xs font-bold text-yellow-500 font-mono block">{genInfo.DT_RECEB || '-'}</span>
            </div>
          </div>

          {(history.Data_Constituicao_Emissor && history.Data_Constituicao_Emissor !== '-') && (
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[var(--border)] text-xs text-[var(--muted)]">
              <div>
                <span>{t.l_inc_date}: </span>
                <strong className="text-[var(--text)]">{history.Data_Constituicao_Emissor}</strong>
              </div>
              <div>
                <span>{t.l_country}: </span>
                <strong className="text-[var(--text)]">{translateTerm(history.Pais_Constituicao_Emissor)}</strong>
              </div>
              <div>
                <span>{t.l_duration}: </span>
                <strong className="text-[var(--text)]">{translateTerm(history.Prazo_Duracao_Emissor)}</strong>
              </div>
            </div>
          )}
        </div>

        {/* Combined Auditor & Responsibles Card */}
        <div className="bg-[var(--card)] border border-[var(--border)] rounded-2xl p-6 shadow-md hover:-translate-y-1 hover:shadow-xl transition-all duration-300 relative overflow-hidden group flex flex-col justify-between">
          <div className="absolute -right-16 -top-16 w-36 h-36 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/15 transition-all duration-300" />

          <div>
            <div>
              <span className="text-[10px] text-[var(--muted)] uppercase font-semibold tracking-wider block mb-1">
                {t.l_aud_firm}
              </span>
              <h4 className="text-sm font-extrabold text-[var(--text)] line-clamp-2">
                {auditor.Auditor || 'PricewaterhouseCoopers'}
              </h4>

              <div className="space-y-2 mt-3 text-[11px]">
                <div className="flex justify-between">
                  <span className="text-[var(--muted)]">{t.l_aud_cvm}</span>
                  <span className="font-bold text-blue-400 font-mono">{auditor.Codigo_CVM_Auditor || '-'}</span>
                </div>
                {auditorResp.Responsavel_Tecnico && auditorResp.Responsavel_Tecnico !== '-' && (
                  <div className="flex justify-between">
                    <span className="text-[var(--muted)]">{t.l_aud_tech}</span>
                    <span className="font-bold text-[var(--text)] text-right max-w-[60%] truncate" title={auditorResp.Responsavel_Tecnico}>
                      {auditorResp.Responsavel_Tecnico}
                    </span>
                  </div>
                )}
                {(auditorResp.Cidade && auditorResp.Cidade !== '-') && (
                  <div className="flex justify-between">
                    <span className="text-[var(--muted)]">{t.l_location}</span>
                    <span className="font-bold text-[var(--text)]">
                      {`${auditorResp.Cidade || ''} - ${auditorResp.Sigla_UF || ''}`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-[var(--border)] my-5" />

            <div>
              <span className="text-[10px] text-[var(--muted)] uppercase font-semibold tracking-wider block mb-3">
                {t.label_filing_responsibles}
              </span>

              <div className="space-y-3">
                {(petrobrasData['responsavel'] || []).map((resp, idx) => {
                  const initials = resp.Nome_Responsavel
                    ? resp.Nome_Responsavel.split(' ').filter(n => n.length > 2).slice(0, 2).map(n => n[0].toUpperCase()).join('')
                    : 'R';
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-2.5 rounded-xl border border-[var(--border)] bg-[var(--accent-soft)]/20 hover:bg-[var(--accent-soft)]/40 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-black text-[10px] shadow-sm">
                        {initials}
                      </div>
                      <div className="space-y-0.5 truncate">
                        <span className="font-bold text-xs text-[var(--text)] block truncate" title={resp.Nome_Responsavel}>
                          {resp.Nome_Responsavel || '-'}
                        </span>
                        <span className="text-[9px] font-semibold text-emerald-400 block truncate">
                          {translateTerm(resp.Cargo_Responsavel)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Capital Structure Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-3 bg-[var(--card)] border border-[var(--border)] rounded-2xl p-6 shadow-md hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h4 className="text-base font-bold text-[var(--text)]">
                  {lang === 'pt' ? 'Estrutura de Capital Social' : 'Share Capital Structure'}
                </h4>
                <p className="text-xs text-[var(--muted)] mt-0.5">
                  {lang === 'pt' ? `Aprovado/Autorizado em: ${primaryCap.Data_Autorizacao_Aprovacao || '-'}` : `Approved on: ${primaryCap.Data_Autorizacao_Aprovacao || '-'}`}
                </p>
              </div>
              <span className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                {translateTerm(primaryCap.Tipo_Capital) || 'Capital Subscrito'}
              </span>
            </div>

            <div className="my-6 p-4 rounded-xl bg-[var(--accent-soft)]/30 border border-[var(--border)] flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <span className="text-[10px] text-[var(--muted)] uppercase font-semibold tracking-wider block">
                  {lang === 'pt' ? 'Valor do Capital Social' : 'Total Capital Value'}
                </span>
                <span className="text-2xl sm:text-3xl font-black text-emerald-500 mt-1 block">
                  {fmtBRL(primaryCap.Valor_Capital)}
                </span>
              </div>
              <div className="text-center sm:text-right">
                <span className="text-[10px] text-[var(--muted)] uppercase font-semibold tracking-wider block">
                  {lang === 'pt' ? 'Volume de Ações' : 'Total Shares Volume'}
                </span>
                <span className="text-lg font-bold text-[var(--text)] mt-1 block">
                  {fmtNum(primaryCap.Quantidade_Total_Acoes)}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-end text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="font-semibold text-[var(--text)]">{lang === 'pt' ? 'Ordinárias (ON)' : 'Common (ON)'}</span>
                </div>
                <span className="font-bold text-[var(--muted)]">
                  {fmtNum(primaryCap.Quantidade_Acoes_Ordinarias)} <span className="text-emerald-500 font-black">({ordPct.toFixed(1)}%)</span>
                </span>
              </div>
              <div className="flex justify-between items-end text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
                  <span className="font-semibold text-[var(--text)]">{lang === 'pt' ? 'Preferenciais (PN)' : 'Preferred (PN)'}</span>
                </div>
                <span className="font-bold text-[var(--muted)]">
                  {fmtNum(primaryCap.Quantidade_Acoes_Preferenciais)} <span className="text-indigo-500 font-black">({prefPct.toFixed(1)}%)</span>
                </span>
              </div>
              <div className="w-full h-3 bg-[var(--accent-soft)] rounded-full overflow-hidden flex">
                <div className="bg-emerald-500 h-full transition-all duration-500" style={{ width: `${ordPct}%` }} title={`ON: ${ordPct.toFixed(1)}%`} />
                <div className="bg-indigo-500 h-full transition-all duration-500" style={{ width: `${prefPct}%` }} title={`PN: ${prefPct.toFixed(1)}%`} />
              </div>
            </div>
          </div>

          <p className="text-[10px] text-[var(--muted)] italic mt-6 border-t border-[var(--border)] pt-3">
            {lang === 'pt'
              ? '* O capital social é totalmente subscrito, emitido e integralizado conforme ata de assembleia.'
              : '* Share capital is fully subscribed, issued, and paid-in according to assembly minutes.'}
          </p>
        </div>
      </div>

      {/* Mercado Estrangeiro */}
      {(() => {
        const meRows = petrobrasData['mercado_estrangeiro'] || [];
        return (
          <Card
            title={lang === 'pt' ? 'Listagem em Mercado Estrangeiro' : 'Foreign Market Listings'}
            subtitle={lang === 'pt' ? `${meRows.length} mercados` : `${meRows.length} markets`}
            refDate={refDate}
          >
            <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs min-w-[600px]">
                <thead>
                  <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Tipo' : 'Type'}</th>
                    <th className="p-3 font-semibold">Ticker</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'País / Mercado' : 'Country / Market'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Início Listagem' : 'Listed Since'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Banco Depositário' : 'Depositary Bank'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {meRows.map((r, idx) => (
                    <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                      <td className="p-3 font-semibold text-[var(--text)]">{r.Valor_Mobiliario || '-'}</td>
                      <td className="p-3">
                        <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-[10px] px-2 py-0.5 rounded font-bold font-mono">
                          {r.Identificacao_Valor_Mobiliario || '-'}
                        </span>
                      </td>
                      <td className="p-3 text-[var(--muted)]">{`${r.Pais_Negociacao || ''} / ${r.Mercado || ''}`}</td>
                      <td className="p-3 font-mono text-[var(--muted)] text-[10px]">{r.Data_Inicio_Listagem || '-'}</td>
                      <td className="p-3 text-[var(--muted)] max-w-[200px] truncate" title={r.Descricao_Banco_Depositario}>{r.Descricao_Banco_Depositario || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })()}

      {/* Política de Negociação & Plano de Recompra */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(() => {
          const polRow = (petrobrasData['politica_negociacao'] || [])[0] || null;
          return (
            <Card
              title={lang === 'pt' ? 'Política de Negociação' : 'Trading Policy'}
              refDate={refDate}
              onClick={polRow ? () => setPolicyModal(polRow) : undefined}
            >
              {polRow ? (
                <div className="space-y-3 text-xs">
                  <div className="flex justify-between py-2 border-b border-[var(--border)]">
                    <span className="text-[var(--muted)]">{lang === 'pt' ? 'Data Aprovação' : 'Approval Date'}</span>
                    <span className="font-bold text-yellow-500 font-mono">{(polRow.Data_Aprovacao || '').split(' ')[0] || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[var(--border)]">
                    <span className="text-[var(--muted)]">{lang === 'pt' ? 'Órgão' : 'Authority'}</span>
                    <span className="font-bold text-[var(--text)]">{polRow.Orgao_Aprovacao || '-'}</span>
                  </div>
                  {polRow.Principais_Caracteristicas && (
                    <div className="pt-1">
                      <span className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider block mb-2">{lang === 'pt' ? 'Principais Características' : 'Main Characteristics'}</span>
                      <p className="text-[var(--text)] leading-relaxed text-[11px] line-clamp-4">{polRow.Principais_Caracteristicas}</p>
                    </div>
                  )}
                  <p className="text-[9px] text-emerald-500 font-semibold pt-1">
                    {lang === 'pt' ? '↗ Clique para ver completo' : '↗ Click to read full policy'}
                  </p>
                </div>
              ) : (
                <p className="text-[var(--muted)] text-xs">{lang === 'pt' ? 'Não disponível para este ano.' : 'Not available for this year.'}</p>
              )}
            </Card>
          );
        })()}

        {(() => {
          const prRows = petrobrasData['plano_recompra'] || [];
          return (
            <Card title={lang === 'pt' ? 'Plano de Recompra de Ações' : 'Share Buyback Plan'} refDate={refDate}>
              {prRows.length > 0 ? (
                <div className="space-y-2 text-xs">
                  {prRows.map((r, idx) => (
                    <div key={idx} className="p-3 border border-[var(--border)] rounded-xl space-y-1">
                      <div className="flex justify-between">
                        <span className="text-[var(--muted)]">{lang === 'pt' ? 'Período' : 'Period'}</span>
                        <span className="font-mono text-yellow-500">{r.Data_Inicio_Recompra || '-'} – {r.Data_Fim_Recompra || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--muted)]">{lang === 'pt' ? 'Qtd. Autorizada' : 'Auth. Quantity'}</span>
                        <span className="font-bold text-emerald-500">{fmtNum(r.Quantidade_Acoes_Autorizadas)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center space-y-2">
                  <span className="text-2xl">—</span>
                  <p className="text-xs text-[var(--muted)]">
                    {lang === 'pt' ? 'Nenhum plano de recompra registrado no FRE para Petrobras.' : 'No share buyback plan registered in FRE for Petrobras.'}
                  </p>
                </div>
              )}
            </Card>
          );
        })()}
      </div>

    </div>
  );
}
